'use client';

import { useRouter } from 'next/navigation';

import { HitType } from '@/modules/common/lib/abtastyStub';

import { DataLayerWhere } from '@/modules/cart/services/data-layer-events/add-to-cart';
import { Button } from '@/modules/common/components/Button';
import ImageServer from '@/modules/common/components/ImageServer';
import { Skeleton } from '@/modules/common/components/skeleton';
import Link from '@/modules/common/components/ui/Link';
import useFeatureFlag from '@/modules/common/hooks/useFeatureFlag';
import { cn } from '@/modules/common/lib/utils';
import { useFilteredCollectionProducts } from '@/modules/product/hooks/useFilteredCollectionProducts';
import { CollectionProduct } from '@/modules/product/models/collection.interface';
import {
  DataLayerFeature,
  addToWishlistDataLayerEvent,
} from '@/modules/product/services/data-layer-events/add-to-wishlist';
import { CollectionDataLayer } from '@/modules/product/services/data-layer-events/utils';
import { ViewMode } from '@/modules/sample-card';
import { AddToShoppingListButton } from '@/modules/shopping-list/components/AddToShoppingListButton';
import { ShoppingListSaveType } from '@/modules/shopping-list/hooks/useShoppingListActions';
import { SaveType } from '@/modules/shopping-list/models/shoppingListTextItem.interface';

import DesignerFlag from '../../collection-designer/DesignerFlag';
import { CollectionSample } from './CollectionSample';

export interface CollectionCardProps {
  title?: string;
  description?: string;
  image?: string;
  productsSku?: string[];
  roomType?: string;
  collectionType?: string;
  roomStyle?: string;
  classNameImg?: string;
  classNameButton?: string;
  slug: string;
  className?: string;
  wrapperClassName?: string;
  where: DataLayerWhere;
  feature?: DataLayerFeature;
  variant?: 'default' | 'plp' | 'brand' | 'editorial' | 'collection-card';
  index?: number;
  itemListId?: string;
  itemListName?: string;
  itemListType?: string;
  mobileCtaFullWidth?: boolean;
  viewMode?: ViewMode;
  isAffiliate?: boolean;
  classNameCollectionName?: string;
  products?: CollectionProduct[];
  designerId?: string;
}

export const CollectionCard = ({
  slug,
  title = 'Midnight Elegance',
  className,
  classNameImg,
  feature = DataLayerFeature.Collections,
  image,
  isAffiliate,
  itemListId = slug,
  itemListName = title,
  itemListType = 'Collection',
  mobileCtaFullWidth = false,
  products = [],
  roomType,
  variant = 'default',
  viewMode = 'default',
  where = DataLayerWhere.GetInspired,
  classNameButton,
  wrapperClassName,
  classNameCollectionName,
  designerId,
}: CollectionCardProps) => {
  const router = useRouter();
  const { sendFeatureFlagTracking } = useFeatureFlag();

  const displayProducts = useFilteredCollectionProducts(products);

  const handleCollectionClick = () => {
    router.push(`/${isAffiliate ? 'affiliate' : 'collection'}/${slug}`);
  };

  const wishlistTrackingCallback = () => {
    addToWishlistDataLayerEvent({
      feature,
      method: 'Single',
      type: ShoppingListSaveType.Collection,
      product: {
        slug,
        title,
      } satisfies CollectionDataLayer,
      where,
      shoppingListProducts: null,
      item_list_id: itemListId,
      item_list_name: itemListName,
      item_list_type: itemListType,
    });

    sendFeatureFlagTracking({
      type: HitType.EVENT,
      category: 'Action Tracking',
      action: 'Collection Saved',
      label: roomType,
    });
  };

  const shoppingListData = {
    name: title,
    key: slug,
    saveType: SaveType.Collection,
  };

  const imageStyles = cn(
    `block aspect-square w-full border border-gray-75 object-cover transition-all duration-300 ${variant === 'default' ? 'rounded-[1.25rem]' : ''}`,
    {
      'rounded-[1.25rem] rounded-b-none': variant === 'brand' || variant === 'default',
      'rounded-b-none lg:rounded-b-[1.25rem]': variant === 'brand',
    },
    classNameImg
  );

  return (
    <div data-testid="collection-card" className={cn('relative', wrapperClassName)}>
      {designerId && (
        <DesignerFlag
          id={designerId}
          isRounded={variant === 'brand' || variant === 'default' ? true : false}
        />
      )}
      <div
        className={cn(
          'flex flex-col items-start',
          {
            '': variant === 'brand',
          },
          className
        )}
      >
        <div className={`relative w-full`}>
          <Link
            href={`/${isAffiliate ? 'affiliate' : 'collection'}/${slug}`}
            className="block h-full w-full object-cover"
            data-testid="collection-link"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              router.push(`/collection/${slug}`);
            }}
          >
            {image && (
              <ImageServer
                src={image}
                width={400}
                alt={title}
                className={imageStyles}
                data-testid="collection-image"
              />
            )}
          </Link>

          <AddToShoppingListButton
            saveType={ShoppingListSaveType.Collection}
            textLineItem={shoppingListData}
            image={image}
            className={cn('right-2 top-2 md:right-3 md:top-3', {
              'right-1 top-1': viewMode === 'compact',
            })}
            callbacks={{ afterAdd: wishlistTrackingCallback }}
            bulkProducts={displayProducts}
          />
        </div>

        <div
          className={cn(
            'rounded-b-3 flex h-full w-full flex-col rounded-b-[1.25rem] border-x border-b border-gray-75 px-4 pb-6 pt-3',
            {
              'rounded-b-[1.25rem] border-gray-100': variant === 'brand',
              'flex-1 border-none p-3': viewMode === 'compact',
              'px-3 pb-3 lg:px-4 lg:pb-6': variant === 'collection-card',
            }
          )}
        >
          <h3
            className={cn(`font-bold leading-[1.375rem] ${classNameCollectionName}`, {
              'order-2': variant === 'brand',
              'mb-1 text-sm leading-4': viewMode === 'compact',
              'h-full': variant === 'collection-card',
            })}
            data-testid="collection-title"
          >
            {title}
          </h3>

          <div
            className={cn('my-3 flex gap-[6px]', {
              'order-1 my-0 mb-3': variant === 'brand',
              'mb-5': viewMode === 'compact',
              'my-0 mb-1 mt-2': variant === 'collection-card',
            })}
          >
            {displayProducts?.slice(0, variant === 'brand' ? 3 : 5)?.map((sample) => (
              <div
                className={cn('h-[42px] w-[42px] sm:h-[64px] sm:w-[64px] md:h-[57px] md:w-[57px]', {
                  'h-6 w-6 sm:h-6 sm:w-6': viewMode === 'compact',
                  'h-8 w-8 rounded-sm': variant === 'collection-card',
                })}
                key={`${sample?.name}-${sample.sku}`}
              >
                <CollectionSample
                  name={sample?.name}
                  image={sample?.image}
                  href={`/sample/${sample.sku}/${sample.ecommName}`}
                  productCategory={sample?.category?.name}
                  size={92}
                  className={cn({
                    'rounded-sm': variant === 'collection-card',
                  })}
                  roundingSize={viewMode === 'compact' ? 's' : 'm'}
                />
              </div>
            ))}
            {variant === 'brand' && displayProducts.length > 3 && (
              <div className="flex h-[42px] w-[42px] items-center justify-center rounded-[10px] bg-gray-75 sm:h-[64px] sm:w-[64px] md:h-[57px] md:w-[57px]">
                <span className="text-xs font-bold">+{displayProducts.length - 3}</span>
              </div>
            )}
          </div>

          <Button
            className={`px-6 ${mobileCtaFullWidth ? 'w-full' : 'w-fit md:w-full lg:w-fit'} ${variant === 'brand' ? 'order-4 mt-3 w-full' : ''} ${viewMode === 'compact' ? 'mt-auto pt-2' : ''} ${variant === 'collection-card' ? 'py-1' : ''} ${classNameButton} `}
            onClick={handleCollectionClick}
            data-testid="explore-collection-button"
            {...(viewMode === 'compact' ?
              {
                size: 'm',
              }
            : {})}
          >
            Sample the Look
          </Button>
        </div>
      </div>
    </div>
  );
};

CollectionCard.displayName = 'CollectionCard';

export const CollectionCardSkeleton = () => {
  return (
    <div className="flex flex-col">
      <Skeleton className="aspect-square w-full rounded-[1.25rem]" />
      <div className="min-h-16 p-3">
        <Skeleton className="mb-2 h-4 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    </div>
  );
};
